<script setup>
import {storeToRefs} from "pinia";
import {useUserTreatmentSessionStore} from "@/stores/userTreatmentSession.store";
import {useRoute} from "vue-router";
import {onMounted, ref} from "vue";
import {useGlobalStore} from "@/stores/global.store";


const route = useRoute();
const userTreatmentSessionStore = useUserTreatmentSessionStore();
const {userTreatmentSession, step, nextId} = storeToRefs(userTreatmentSessionStore);

const globalStore = useGlobalStore();

onMounted(async () => {
    globalStore.loading = true;
    await userTreatmentSessionStore.fetchOne(route.params.id);
    await userTreatmentSessionStore.nextUserTreatmentSession(route.params.id);
    globalStore.loading = false;
});

const findUserResource = (userTreatmentResources, resourceId, resourceType) => {
    return userTreatmentResources.find(
        item => item.resource_type === resourceType && item.resource_id === resourceId);
}

const findUserResourceId = (userTreatmentResources, resourceId, resourceType) => {
    const userTreatmentResource = findUserResource(userTreatmentResources, resourceId, resourceType)
    return userTreatmentResource ? userTreatmentResource.user_resource_id : null;
}

const findUserResourceStatus = (userTreatmentResources, resourceId, resourceType) => {
    const userTreatmentResource = findUserResource(userTreatmentResources, resourceId, resourceType)
    return userTreatmentResource ? userTreatmentResource.status : null;
}

const findResourceIcon = (resourceType) => {
    switch (resourceType) {
        case 'exercise':
            return 'video-o';
        case 'instruction':
            return 'comment-o';
        case 'survey':
            return 'records';
        case 'form':
            return 'orders-o';
        case 'file':
            return 'link-o';
    }
}

const findResourceRoute = (userTreatmentResources, resourceId, resourceType) => {
    const userResourceId = findUserResourceId(userTreatmentResources, resourceId, resourceType);
    switch (resourceType) {
        case 'exercise':
            return '/exercise/' + userResourceId;
        case 'instruction':
            return '/instruction/' + userResourceId;
        case 'survey':
            return '/survey/' + userResourceId;
        case 'form':
            return '/form/' + userResourceId;
        case 'file':
            return '/file/' + userResourceId;
    }
}

const name = ref(1);

const updateResource = async () => {
    await userTreatmentSessionStore.updateResource(route.params.id);
}

const redirectNextSession = (sessionId) => {
    window.location.href = '/session/' + sessionId;
}

const redirectHome = () => {
    window.location.href = '/';
}

</script>

<template>
    <van-space direction="vertical" fill>
        <van-cell-group inset>
            <van-steps :active="step">
                <van-step>Not Started</van-step>
                <van-step>In Progress</van-step>
                <van-step>Awaiting</van-step>
                <van-step>Completed</van-step>
            </van-steps>
        </van-cell-group>

        <van-cell-group inset>
            <van-collapse v-model="name" accordion>
                <van-collapse-item name="1">
                    <template #title>
                        <b>{{ userTreatmentSession.treatment_session.name }}</b>
                    </template>
                    {{ userTreatmentSession.treatment_session.description }}
                </van-collapse-item>
            </van-collapse>
            <van-cell title="Created" :value="userTreatmentSession.created_at_formatted"/>
            <van-cell title="Plan" :value="userTreatmentSession?.user_treatment_plan?.treatment_plan?.name"/>
            <van-cell title="Scheduled" :value="userTreatmentSession.schedule_at_formatted"/>
            <van-cell title="Counselor">
                <template #label>
                    {{ userTreatmentSession.staff_user }}
                </template>
            </van-cell>
        </van-cell-group>

        <van-row justify="center">
            <b>Goals</b>
        </van-row>
        <van-cell-group inset>
            <van-cell
                v-for="goal in userTreatmentSession.treatment_session.treatment_session_goals"
                :key="goal.id"
                icon="certificate">
                <template #title>
                    {{ goal.name }}
                </template>
            </van-cell>
        </van-cell-group>

        <van-row justify="center">
            <b>Sections</b>
        </van-row>
        <van-cell-group inset v-for="section in userTreatmentSession.treatment_session.treatment_session_sections"
                        :key="section.id">
            <van-cell :label="section.type + ': ' + section.length_in_min + ' mins'">
                <template #title>
                    {{ section.name }}
                </template>
            </van-cell>
            <van-cell v-for="resource in section.treatment_resources"
                      :key="resource.id"
                      :icon="findResourceIcon(resource.resource_type)"
                      :value="resource.name"
                      is-link
                      :to="findResourceRoute(userTreatmentSession.user_treatment_resources, resource.resource_id, resource.resource_type)"
            >
                <template #title>
                    <van-tag plain round size="medium" type="primary">
                        {{
                            findUserResourceStatus(userTreatmentSession.user_treatment_resources, resource.resource_id, resource.resource_type)
                        }}
                    </van-tag>
                </template>
            </van-cell>
        </van-cell-group>

        <van-row>&nbsp;</van-row>

        <van-cell-group inset>
            <div v-if="userTreatmentSession.treatment_session.treatment_session_quiz_id && userTreatmentSession.user_treatment_session_quiz_id">
                <div v-if="userTreatmentSession.is_all_resource_completed">
                    <van-button block round type="success"
                                :to="'/quiz/' + userTreatmentSession.user_treatment_session_quiz_id">
                        Start Quiz
                    </van-button>
                </div>
                <div v-else-if="userTreatmentSession.status === 'Completed'">
                    <div v-if="nextId">
                        <van-button block round type="success" @click="redirectNextSession(nextId)">
                            Next Session
                        </van-button>
                    </div>
                </div>
            </div>
            <div v-else>
                <div v-if="userTreatmentSession.is_all_resource_completed">
                    <van-button block round type="success"
                                @click="updateResource">
                        Submit for Approval
                    </van-button>
                </div>
                <div v-else-if="userTreatmentSession.status === 'Completed'">
                    <div v-if="nextId">
                        <van-button block round type="success" @click="redirectNextSession(nextId)">
                            Next Session
                        </van-button>
                    </div>
                </div>
            </div>
            <van-row>&nbsp;</van-row>
            <div>
                <van-button block plain round type="warning"
                            @click="redirectHome">
                    Back to Schedule
                </van-button>
            </div>
        </van-cell-group>

    </van-space>
</template>

